import { PaginationMetadata } from './pagination.interface'

export type FinancialObligationKind = 'invoice' | 'order'

export interface FinancialObligationAmounts {
  billed_cents: number
  paid_cents: number
  discounted_cents: number
  credited_cents: number
  refund_adjustments_cents: number
  outstanding_cents: number
}

export interface FinancialObligationData {
  id: number
  kind: FinancialObligationKind
  sale_number: string
  description: string
  recipient_name: string
  status: string
  monetary_status: string
  display_status: string
  due_date: string | null
  currency: string
  amounts: FinancialObligationAmounts
  has_discounts: boolean
  has_credits: boolean
  auto_pay: FinancialObligationAutoPay | null
  payment_url: string
}

export interface FinancialObligationAutoPay {
  account_type: string
  last_four: string
}

export interface FinancialObligationMetadata {
  pagination: PaginationMetadata
}

export type FinancialObligationList = FinancialObligation[] & {
  metadata: FinancialObligationMetadata
}

export class FinancialObligation implements FinancialObligationData {
  private static readonly ORDER_DETAIL_QUERY_PARAMS = { returnTo: '/user/bills' }

  public id: number
  public kind: FinancialObligationKind
  public sale_number: string
  public description: string
  public recipient_name: string
  public status: string
  public monetary_status: string
  public display_status: string
  public due_date: string | null
  public currency: string
  public amounts: FinancialObligationAmounts
  public has_discounts: boolean
  public has_credits: boolean
  public auto_pay: FinancialObligationAutoPay | null
  public payment_url: string

  public constructor(data: FinancialObligationData) {
    Object.assign(this, data)
  }

  public get billedAmount(): number {
    return this.amounts.billed_cents / 100
  }

  public get paidAmount(): number {
    return this.amounts.paid_cents / 100
  }

  public get discountedAmount(): number {
    return this.amounts.discounted_cents / 100
  }

  public get creditedAmount(): number {
    return this.amounts.credited_cents / 100
  }

  public get refundAdjustmentsAmount(): number {
    return this.amounts.refund_adjustments_cents / 100
  }

  public get outstandingAmount(): number {
    return this.amounts.outstanding_cents / 100
  }

  public get detailRoute(): string {
    const resource = this.kind === 'order' ? 'orders' : 'bills'
    return `/user/${resource}/${this.sale_number}`
  }

  public get detailQueryParams(): Record<string, string> | null {
    return this.kind === 'order'
      ? FinancialObligation.ORDER_DETAIL_QUERY_PARAMS
      : null
  }

  public get showMakePayment(): boolean {
    return Boolean(this.payment_url) && (
      (this.display_status === 'open' && !this.auto_pay) ||
      this.display_status === 'past_due'
    )
  }

  public get statusTranslationSuffix(): string {
    if (this.display_status === 'open' && this.auto_pay) return 'autopay'
    if (this.display_status === 'voided') return 'voided'
    return 'paid_in_full'
  }
}

export interface FinancialObligationPaymentData {
  id: number
  obligation_id: number
  kind: FinancialObligationKind
  sale_number: string
  description: string
  currency: string
  amount_cents: number
  due_date: string | null
  due_date_days_past: number
  status: string
  auto_pay: FinancialObligationAutoPay | null
  payment_url: string
}

export class FinancialObligationPayment implements FinancialObligationPaymentData {
  public id: number
  public obligation_id: number
  public kind: FinancialObligationKind
  public sale_number: string
  public description: string
  public currency: string
  public amount_cents: number
  public due_date: string | null
  public due_date_days_past: number
  public status: string
  public auto_pay: FinancialObligationAutoPay | null
  public payment_url: string

  public constructor(data: FinancialObligationPaymentData) {
    Object.assign(this, data)
  }
}
